import React, { useCallback, useEffect, useState } from "react";
import { FiArrowLeft, FiLifeBuoy, FiMoreHorizontal, FiUser } from "react-icons/fi";
import { ClientNameLink } from "../../../common/comms/ClientProfileProvider";
import { Composer } from "../Messages/Composer";
import { useRoomMessages } from "../Messages/useRoomMessages";
import { TicketThread } from "./TicketThread";
import { SlaStrip, reopenWindowLeft } from "./SlaStrip";
import { MacroPicker } from "./MacroPicker";
import { TagEditor } from "./TagEditor";
import { PRIORITIES, PRIORITY_LABELS } from "./supportEnums";
import {
  extractTicketDetail,
  getAssignee,
  getAssigneeId,
  getCsat,
  getCsatFeedback,
  getDisplayId,
  getMergedIntoRoomId,
  getPriority,
  getTags,
  getTicketClientName,
  getTicketStatus,
  isMerged,
  isSnoozed,
} from "./ticketUtils";

/** Overflow menu for the actions that don't earn a permanent button. */
const OverflowMenu = ({ items }) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return undefined;
    const onOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEscape = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onEscape);
    };
  }, [open]);

  if (items.length === 0) return null;
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More ticket actions"
        className="inline-flex h-7 items-center rounded-md border border-gray-200 px-2 text-gray-600 transition-colors hover:bg-gray-50"
      >
        <FiMoreHorizontal size={15} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-1 w-48 overflow-hidden rounded-md border border-gray-200 bg-white py-1 shadow-lg"
        >
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={() => {
                setOpen(false);
                item.onClick();
              }}
              className="block w-full px-3 py-2 text-left text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/** Header button styles, so the action row stays one consistent size. */
const actionBtn =
  "inline-flex h-7 items-center rounded-md px-2.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50";
const ghostBtn = `${actionBtn} border border-gray-200 text-gray-700 hover:bg-gray-50`;

/**
 * The ticket pane: header (identity, classification, both SLA clocks, the
 * action row), the email-style thread, and the reply composer.
 *
 * When the room answers 403 the pane still renders everything it legitimately
 * knows — the ticket record is readable by any employee — and swaps the thread
 * for a pick-up prompt. That is the unassigned-pool case, not an error: reading
 * the conversation requires *room* participation, which assignment grants.
 *
 * @param {Object} props
 * @param {string|null} props.roomId
 * @param {string|number} [props.viewerUserId]
 * @param {Object|null} props.detail - Ticket detail from `getTicket`.
 * @param {Object|null} [props.headerTicket] - Queue row, shown before detail lands.
 * @param {(action: string) => void} [props.onRequestAction]
 * @param {() => void} [props.onResolve]
 * @param {() => void} [props.onReopen]
 * @param {() => void} [props.onUnsnooze]
 * @param {(payload: Object) => void} [props.onPatch]
 * @param {() => void} [props.onPickUp]
 * @param {string|null} [props.busy]
 * @param {React.ReactNode} [props.banner] - Viewing strip.
 * @param {() => void} [props.onBack] - Stacked layouts only.
 * @param {() => void} [props.onOpenRail] - When the context rail isn't pinned.
 */
export function TicketPane({
  roomId,
  viewerUserId,
  detail,
  headerTicket,
  onRequestAction,
  onResolve,
  onReopen,
  onUnsnooze,
  onPatch,
  onPickUp,
  busy = null,
  banner,
  onBack,
  onOpenRail,
}) {
  const [resolveOnSend, setResolveOnSend] = useState(false);
  const [prefill, setPrefill] = useState(null);

  const room = useRoomMessages(roomId, viewerUserId);

  // The thread 403s until you're a participant, and *picking the ticket up is
  // what makes you one* — but the room id never changed, so nothing would
  // re-attempt the load. Retry the moment the ticket reads as ours, which also
  // covers a manager assigning it to us while we're looking at it.
  const assigneeId = getAssigneeId(extractTicketDetail(detail) || headerTicket || {});
  const isMine =
    assigneeId != null && viewerUserId != null && String(assigneeId) === String(viewerUserId);
  const { forbidden, retry } = room;
  useEffect(() => {
    if (!forbidden || !isMine) return undefined;
    retry();
    // The header flips to "assigned" optimistically, which can beat the
    // backend committing room membership — so one delayed second attempt
    // covers the lag. If the first succeeds, `forbidden` flips and this
    // cleanup cancels the timer before it fires.
    const timer = setTimeout(retry, 1200);
    return () => clearTimeout(timer);
  }, [forbidden, isMine, retry]);

  const handleMacro = useCallback(
    (body) => setPrefill({ text: body, nonce: Date.now() }),
    []
  );

  const handleSend = useCallback(
    async (payload) => {
      await room.send(payload);
      // "Resolve on send" is the one compound action agents actually want: the
      // reply *is* the resolution. An internal note never resolves — it isn't
      // customer-visible and doesn't even stop the SLA clock.
      //
      // Routed through the same handler as the Resolve button rather than
      // calling the endpoint here, so it gets the toast, the resync and the
      // optimistic header update — and can't resolve the ticket twice.
      if (resolveOnSend && !payload.coach_only) {
        setResolveOnSend(false);
        await onResolve?.();
      }
    },
    [room, resolveOnSend, onResolve]
  );

  if (!roomId) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-gray-400">
        <FiLifeBuoy size={40} />
        <p className="text-sm">Select a ticket to open it</p>
      </div>
    );
  }

  const source = extractTicketDetail(detail) || headerTicket || {};
  const workflow = getTicketStatus(source);
  const resolved =
    workflow === "RESOLVED" || source.status === "resolved" || Boolean(source.resolved_at);
  const closed = workflow === "CLOSED";
  const merged = isMerged(source);
  const snoozed = isSnoozed(source);
  const locked = merged || closed;
  const unassigned = getAssigneeId(source) == null && !getAssignee(source);
  const priority = getPriority(source);
  const csat = getCsat(source);
  const csatFeedback = getCsatFeedback(source);
  const reopenLeft = resolved ? reopenWindowLeft(source.resolved_at) : null;

  const menuItems = [
    {
      key: "escalate",
      label: "Escalate…",
      onClick: () => onRequestAction?.("escalate"),
      disabled: locked || resolved,
    },
    unassigned
      ? null
      : {
          key: "unassign",
          label: "Return to pool…",
          onClick: () => onRequestAction?.("unassign"),
          disabled: locked,
        },
    snoozed
      ? { key: "unsnooze", label: "Wake now", onClick: () => onUnsnooze?.(), disabled: locked }
      : {
          key: "snooze",
          label: "Snooze…",
          onClick: () => onRequestAction?.("snooze"),
          disabled: locked || resolved,
        },
    {
      key: "merge",
      label: "Merge into…",
      onClick: () => onRequestAction?.("merge"),
      disabled: locked,
    },
  ].filter(Boolean);

  const pickUpPrompt = (
    <div className="max-w-xs px-6 py-10 text-center">
      <FiLifeBuoy aria-hidden="true" size={28} className="mx-auto text-gray-300" />
      {isMine ? (
        <>
          {/* Ours, but the room hasn't caught up. The automatic retry above
              usually resolves this before it's ever seen; the button exists so
              a lagging read model isn't a dead end — there is no Pick up to
              offer once the ticket is already assigned. */}
          <p className="mt-2 text-sm font-semibold text-gray-700">
            This ticket is yours — loading the conversation
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Room access follows assignment a moment later.
          </p>
          <button
            type="button"
            onClick={retry}
            className={`mt-3 ${ghostBtn}`}
          >
            Try again
          </button>
        </>
      ) : (
        <>
          <p className="mt-2 text-sm font-semibold text-gray-700">
            Pick up this ticket to read the conversation
          </p>
          <p className="mt-1 text-xs text-gray-500">
            The ticket record is open to everyone, but its messages are only visible to
            participants — taking ownership adds you to the room.
          </p>
          {onPickUp && !resolved && !locked && (
            <button
              type="button"
              onClick={onPickUp}
              disabled={busy === "pickup"}
              className={`mt-3 ${actionBtn} bg-orange-primary text-white hover:brightness-95`}
            >
              {busy === "pickup" ? "Picking up…" : "Pick up"}
            </button>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex-shrink-0 border-b border-gray-200 px-3.5 py-2.5">
        <div className="flex items-baseline gap-2">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              aria-label="Back to the queue"
              className="-ml-1 self-center rounded p-1 text-gray-500 hover:bg-gray-100"
            >
              <FiArrowLeft size={15} />
            </button>
          )}
          <span className="font-mono text-xs font-bold text-gray-400">
            #{getDisplayId(source)}
          </span>
          <h2 className="m-0 min-w-0 flex-1 truncate text-sm font-bold text-gray-900">
            {source.subject || "Support ticket"}
          </h2>
          {onOpenRail && (
            <button
              type="button"
              onClick={onOpenRail}
              aria-label="Open requester context"
              className="self-center rounded p-1 text-gray-500 hover:bg-gray-100"
            >
              <FiUser size={15} />
            </button>
          )}
        </div>

        {/* Classification line */}
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase tracking-wide">
          <span className={resolved ? "text-success" : "text-info"}>
            {merged ? "Merged" : resolved ? "Resolved" : snoozed ? "Snoozed" : "Open"}
          </span>
          <span className="text-gray-300">/</span>
          <span className={priority === "urgent" ? "text-danger-dark" : "text-gray-700"}>
            {PRIORITY_LABELS[priority] || priority}
          </span>
          {(source.department || source.type) && (
            <>
              <span className="text-gray-300">/</span>
              <span className="text-gray-700">
                {[source.department, source.type].filter(Boolean).join(" · ")}
              </span>
            </>
          )}
          {getTags(source).length > 0 && (
            <>
              <span className="text-gray-300">/</span>
              <span className="font-medium normal-case tracking-normal text-gray-500">
                {getTags(source).join(", ")}
              </span>
            </>
          )}
          {source.client_id && (
            <span className="ml-auto font-medium normal-case tracking-normal text-gray-500">
              <ClientNameLink
                clientId={source.client_id}
                name={getTicketClientName(source)}
              />
            </span>
          )}
        </div>

        <SlaStrip ticket={source} resolved={resolved} csat={csat} />

        {merged && (
          <p className="mt-2 rounded-md bg-gray-100 px-2.5 py-1.5 text-[11px] text-gray-600">
            Merged into <span className="font-bold">#{getMergedIntoRoomId(source)}</span>.
            Its messages stayed here; this room is archived and write-frozen.
          </p>
        )}

        {/* Action row */}
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          {onPickUp && unassigned && !resolved && !locked && (
            <button
              type="button"
              onClick={onPickUp}
              disabled={busy === "pickup"}
              className={`${actionBtn} bg-orange-primary text-white hover:brightness-95`}
            >
              {busy === "pickup" ? "Picking up…" : "Pick up"}
            </button>
          )}

          {onPatch && !locked && (
            <select
              value={priority}
              onChange={(e) => onPatch({ priority: e.target.value })}
              disabled={busy === "patch"}
              aria-label="Ticket priority"
              className={`${ghostBtn} pr-1`}
            >
              {PRIORITIES.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label}
                </option>
              ))}
            </select>
          )}

          <MacroPicker
            department={source.department}
            onPick={handleMacro}
            disabled={locked || room.forbidden}
          />
          {onPatch && (
            <TagEditor
              tags={getTags(source)}
              onChange={(tags) => onPatch({ tags })}
              disabled={locked || busy === "patch"}
            />
          )}
          <OverflowMenu items={menuItems} />

          {resolved && !closed ? (
            <div className="ml-auto flex items-center gap-2">
              {reopenLeft && (
                <span className="text-[10px] text-gray-400">
                  Reopen window closes in {reopenLeft}
                </span>
              )}
              <button
                type="button"
                onClick={onReopen}
                disabled={busy === "reopen"}
                className={ghostBtn}
              >
                {busy === "reopen" ? "Reopening…" : "Reopen"}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={onResolve}
              disabled={locked || busy === "resolve"}
              className={`ml-auto ${actionBtn} bg-success text-white hover:brightness-95`}
            >
              {busy === "resolve" ? "Resolving…" : "Resolve"}
            </button>
          )}
        </div>
      </header>

      {banner}

      <TicketThread
        messages={room.messages}
        participants={room.participants}
        forbidden={room.forbidden}
        hasMore={room.hasMore}
        loadingOlder={room.loadingOlder}
        onLoadOlder={room.loadOlder}
        pickUpPrompt={pickUpPrompt}
        csat={
          csatFeedback && { ...csatFeedback, name: getTicketClientName(source) }
        }
      />

      {!room.forbidden && !locked && (
        <div className="flex-shrink-0 border-t border-gray-200">
          <Composer
            disabled={locked}
            canPublish={room.canPublish}
            modes={["client", "internal"]}
            participants={room.participants}
            onSubmit={handleSend}
            initialText={prefill?.text}
            initialTextNonce={prefill?.nonce}
          />
          {!resolved && (
            <label className="flex items-center gap-1.5 px-3.5 pb-2.5 text-[11px] text-gray-500">
              <input
                type="checkbox"
                checked={resolveOnSend}
                onChange={(e) => setResolveOnSend(e.target.checked)}
                className="h-3 w-3 accent-orange-primary"
              />
              Resolve on send
              <span className="text-gray-400">· posts the CSAT survey too</span>
            </label>
          )}
        </div>
      )}
    </div>
  );
}

export default TicketPane;
